import { ColorSpot, getContrastColor } from "./colorUtils";

const drawColorSpots = (
  ctx: CanvasRenderingContext2D,
  colorSpots: ColorSpot[],
  image: HTMLImageElement,
  canvasWidth: number,
  canvasHeight: number
) => {
  const spotRadius = Math.round(canvasWidth * 0.025);
  colorSpots.forEach((spot, index) => {
    const x = (spot.x / image.width) * canvasWidth;
    const y = (spot.y / image.height) * canvasHeight;

    ctx.beginPath();
    ctx.arc(x, y, spotRadius, 0, Math.PI * 2);
    ctx.fillStyle = spot.color;
    ctx.fill();
    ctx.strokeStyle = "white";
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.fillStyle = "white";
    ctx.font = `bold ${Math.round(spotRadius * 0.8)}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText((index + 1).toString(), x, y);
  });
};

const triggerDownload = (dataUrl: string, filename: string) => {
  const link = document.createElement("a");
  link.download = filename;
  link.href = dataUrl;
  link.click();
};

export const exportWithBackground = (
  image: HTMLImageElement,
  colorSpots: ColorSpot[],
  format: "png" | "jpg" = "png"
) => {
  const canvas = document.createElement("canvas");
  const aspectRatio = image.width / image.height;
  const canvasWidth = 1920;
  const canvasHeight = Math.round(canvasWidth / aspectRatio);

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d");

  if (ctx) {
    ctx.drawImage(image, 0, 0, canvasWidth, canvasHeight);
    ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    drawColorSpots(ctx, colorSpots, image, canvasWidth, canvasHeight);

    const mimeType = format === "jpg" ? "image/jpeg" : "image/png";
    const dataUrl = canvas.toDataURL(mimeType, format === "jpg" ? 0.95 : undefined);
    triggerDownload(dataUrl, `color-palette-with-bg.${format}`);
  }
};

export const exportTransparent = (
  image: HTMLImageElement,
  colorSpots: ColorSpot[],
  format: "png" | "jpg" = "png"
) => {
  const canvas = document.createElement("canvas");
  const aspectRatio = image.width / image.height;
  const canvasWidth = 1920;
  const canvasHeight = Math.round(canvasWidth / aspectRatio);

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d");

  if (ctx) {
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    drawColorSpots(ctx, colorSpots, image, canvasWidth, canvasHeight);

    const mimeType = format === "jpg" ? "image/jpeg" : "image/png";
    const dataUrl = canvas.toDataURL(mimeType, format === "jpg" ? 0.95 : undefined);
    triggerDownload(dataUrl, `color-palette.${format}`);
  }
};

export const exportWhiteBackground = (
  image: HTMLImageElement,
  colorSpots: ColorSpot[]
) => {
  const canvas = document.createElement("canvas");
  const aspectRatio = image.width / image.height;
  const canvasWidth = 1920;
  const canvasHeight = Math.round(canvasWidth / aspectRatio);

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d");

  if (ctx) {
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    ctx.strokeStyle = "#ddd";
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, canvasWidth, canvasHeight);

    drawColorSpots(ctx, colorSpots, image, canvasWidth, canvasHeight);

    const dataUrl = canvas.toDataURL("image/png");
    triggerDownload(dataUrl, "color-palette-white-bg.png");
  }
};

export const exportColorSwatches = (colorSpots: ColorSpot[]) => {
  if (colorSpots.length === 0) return;

  const uniqueColors = Array.from(new Set(colorSpots.map((spot) => spot.color)));

  const canvas = document.createElement("canvas");
  const colorsPerRow = 5;
  const rows = Math.ceil(uniqueColors.length / colorsPerRow);
  const spotWidth = 600;
  const spotHeight = 300;
  const canvasWidth = spotWidth * colorsPerRow;
  const canvasHeight = spotHeight * rows;
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d");

  if (ctx) {
    uniqueColors.forEach((color, index) => {
      const row = Math.floor(index / colorsPerRow);
      const col = index % colorsPerRow;
      const x = col * spotWidth;
      const y = row * spotHeight;

      ctx.fillStyle = color;
      ctx.fillRect(x, y, spotWidth, spotHeight);

      ctx.fillStyle = getContrastColor(color);
      ctx.font = "bold 36px Arial";
      ctx.textAlign = "center";
      ctx.fillText(color, x + spotWidth / 2, y + spotHeight - 30);
    });

    const dataUrl = canvas.toDataURL("image/png");
    triggerDownload(dataUrl, "color-palette.png");
  }
};
